'use client'

import { ControllerRenderProps, Path } from 'react-hook-form'

import {
  getPayoutsFilterMetadata,
  GetPayoutsFilterMetadataParams,
  useGetPayoutsFilterMetadata,
} from '@api/orval/apiHooks'
import { AllFiltersArea, FieldConfig, type FilterFieldOptions } from '@components/Filters/filter.types'
import { leftSideGroupStyle, rightSideGroupStyle } from '@components/Filters/FilterForm'
import { Input, MultiSelect } from '@fp/ui-kit'
import { convertToValueLabel } from '@lib/convertToValueLabel'
import { getInputValue } from '@lib/getFilterInputValue'
import type { IsoDateRange } from '@lib/tz'
import { DatePicker } from '@ui/DateRangePicker'
import { InputWithDropdown, type UserType } from '@ui/InputWithDropdown'

type PayoutFormValues = {
  risk_statuses?: string[]
  payout_cycles?: string[]
  account_types?: string[]
  template_ids?: string[]
  payment_methods?: string[]
  statuses?: string[]
  payout_types?: string[]
  amount_from?: number
  amount_to?: number
  last_update?: IsoDateRange

  filter_user_id?: number | null
  assignee_id?: number | null
  search_field?: string
  tag_list: string[]
}

export const unassignedUserOption: UserType = {
  id: 'null' as unknown as number,
  first_name: 'Unassigned',
  last_name: '',
  email: 'Unassigned',
}

export const buildPayoutsConfig = ({ isPrimeMode = false } = {}) => ({
  filterTitle: 'Filter Payouts',
  filtersArea: 'payouts' as AllFiltersArea,
  initialValues: {
    filter_user_id: undefined,
    assignee_id: undefined,
    search_field: '',
    last_update: { from: undefined, to: undefined },
    statuses: [],
    payout_types: [],
    payment_methods: [],
    amount_from: undefined,
    amount_to: undefined,
    risk_statuses: [],
    payout_cycles: [],
    account_types: [],
    template_ids: [],
    tag_list: [],
  },
  loadOptions: async (query: GetPayoutsFilterMetadataParams) => {
    const data = await getPayoutsFilterMetadata(query)

    return {
      count: data?.count,
      bulk_actions: data?.bulk_actions,
      options: {
        payout_types: (data?.fields.payout_types || []).map(convertToValueLabel),
        statuses: (data?.fields.statuses || []).map(convertToValueLabel),
        payment_methods: (data?.fields.payment_methods || []).map(convertToValueLabel),
        templates: (data?.fields.templates || []).map((t) => ({ value: String(t.value), label: t.name })),

        risk_statuses: (data?.fields.risk_statuses || []).map(convertToValueLabel),
        payout_cycles: (data?.fields.payout_cycles || []).map(convertToValueLabel),
        account_types: (data?.fields.account_types || []).map(convertToValueLabel),
        tags: (data?.fields.tags || []).map(convertToValueLabel),
      },
    }
  },
  fields: (options: FilterFieldOptions): FieldConfig<PayoutFormValues>[] => [
    {
      name: 'search_field',
      label: 'Payouts',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) => (
        <Input
          {...fieldProps}
          placeholder='Search'
          value={typeof fieldProps.value === 'string' ? fieldProps.value : ''}
        />
      ),
    },

    {
      name: 'filter_user_id',
      label: 'Search by user',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) => (
        <InputWithDropdown
          placeholder='User email or name'
          onSelect={fieldProps.onChange}
          useMetadataHook={useGetPayoutsFilterMetadata}
        />
      ),
    },

    {
      name: 'assignee_id',
      label: 'Assignee',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) => (
        <InputWithDropdown
          placeholder='Assignee email or name'
          metadataPathKey='assignee_search_path'
          urlUserParam='assignee_id'
          onSelect={fieldProps.onChange}
          useMetadataHook={useGetPayoutsFilterMetadata}
          additionalOptions={[unassignedUserOption] as UserType[]}
        />
      ),
    },

    {
      name: 'statuses',
      label: 'Statuses',
      section: 'PAYOUT PROPERTIES',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.statuses) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.statuses}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },
    {
      name: 'payout_types',
      label: 'Payout Types',
      section: 'PAYOUT PROPERTIES',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.payout_types) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.payout_types}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },
    {
      name: 'payment_methods',
      label: 'Payment Methods',
      section: 'PAYOUT PROPERTIES',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.payment_methods) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.payment_methods}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
            // CustomFirstOptions={({ toggleValue, value, onlyOneSelect }) => {
            //   const name = 'Unassigned'

            //   return (
            //     <MSItemComponent
            //       name={name}
            //       value={value}
            //       onlyOneSelect={onlyOneSelect}
            //       toggleValue={toggleValue}
            //       option={{ value: name, label: name }}
            //     />
            //   )
            // }}
          />
        ),
    },
    {
      name: 'tag_list',
      label: 'Tags',
      section: 'PAYOUT PROPERTIES',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.tags) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.tags}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },

    {
      name: 'account_types',
      label: isPrimeMode ? 'Account Type (locked to Prime)' : 'Account Type',
      section: 'ACCOUNT & RISK',
      disabled: isPrimeMode,
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.account_types) && (
          <MultiSelect
            {...fieldProps}
            placeholder={isPrimeMode ? 'Prime' : 'All'}
            options={options.account_types}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
            disabled={isPrimeMode}
          />
        ),
    },
    {
      name: 'payout_cycles',
      label: 'Payout Cycle',
      section: 'ACCOUNT & RISK',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.payout_cycles) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.payout_cycles}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },
    {
      name: 'risk_statuses',
      label: 'Risk Status',
      section: 'ACCOUNT & RISK',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.risk_statuses) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.risk_statuses}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },
    {
      name: 'template_ids',
      label: 'Templates',
      section: 'ACCOUNT & RISK',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) =>
        Array.isArray(options.templates) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.templates}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },

    {
      name: 'last_update',
      label: 'Last update from - to',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) => (
        <DatePicker value={fieldProps.value as IsoDateRange} onChange={fieldProps.onChange} />
      ),
    },
    {
      name: 'amount_from',
      label: 'Amount from - to',
      group: 'amount',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) => (
        <Input
          {...fieldProps}
          className={leftSideGroupStyle}
          placeholder='Min'
          type='number'
          min={0}
          value={getInputValue(fieldProps.value)}
        />
      ),
    },
    {
      name: 'amount_to',
      label: '',
      group: 'amount',
      element: (fieldProps: ControllerRenderProps<PayoutFormValues, Path<PayoutFormValues>>) => (
        <Input
          {...fieldProps}
          className={rightSideGroupStyle}
          placeholder='Max'
          type='number'
          min={0}
          value={getInputValue(fieldProps.value)}
        />
      ),
    },
  ],
})
